import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError, peso } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Plus, X, Star, Users, Wallet, Sparkles, Trash2, Pencil, Calendar, Tag } from "lucide-react";

const ALLOWED_TABLE_TYPES = ["regular", "vip", "bar_counter", "standing", "high_table"];

const blankInclusion = () => ({ product_id: "", quantity: 1, notes: "" });

const buildBlank = (branchId) => ({
  branch_id: branchId || "",
  code: "",
  name: "",
  description: "",
  minimum_spend: 5000,
  required_deposit: 1000,
  guest_limit: 6,
  vip_only: false,
  allowed_table_types: [],
  event_id: "",
  valid_from: "",
  valid_to: "",
  active: true,
  inclusions: [blankInclusion()],
});


export default function VipPackagesPage() {
  const { hasPerm, branchId } = useAuth();
  const [packages, setPackages] = useState([]);
  const [products, setProducts] = useState([]);
  const [branches, setBranches] = useState([]);
  const [events, setEvents] = useState([]);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(() => buildBlank(branchId));
  const [busy, setBusy] = useState(false);

  const canManage = hasPerm && hasPerm("settings.manage");

  const load = async () => {
    try {
      const [{ data: pkgs }, { data: prods }, { data: brs }, { data: evs }] = await Promise.all([
        api.get("/vip-packages", { params: branchId ? { branch_id: branchId } : {} }),
        api.get("/products", { params: { active_only: true } }),
        api.get("/branches"),
        api.get("/events", { params: branchId ? { branch_id: branchId } : {} }).catch(() => ({ data: [] })),
      ]);
      setPackages(pkgs);
      setProducts(prods.filter((p) => p.active !== false));
      setBranches(brs);
      setEvents(evs);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  useEffect(() => { load(); /* eslint-disable-line */ }, [branchId]);

  const openCreate = () => { setEditing(null); setForm(buildBlank(branchId)); setCreating(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      branch_id: p.branch_id || branchId || "",
      code: p.code,
      name: p.name,
      description: p.description || "",
      minimum_spend: p.minimum_spend,
      required_deposit: p.required_deposit,
      guest_limit: p.guest_limit,
      vip_only: !!p.vip_only,
      allowed_table_types: p.allowed_table_types || [],
      event_id: p.event_id || "",
      valid_from: p.valid_from || "",
      valid_to: p.valid_to || "",
      active: p.active !== false,
      inclusions: (p.inclusions && p.inclusions.length)
        ? p.inclusions.map((i) => ({
            product_id: i.product_id || "",
            quantity: i.quantity ?? 1,
            notes: i.notes || "",
            // Preserve legacy free-text rows so they render as read-only.
            _legacy_name: !i.product_id ? (i.name || "") : "",
          }))
        : [blankInclusion()],
    });
    setCreating(true);
  };

  const setIncField = (idx, key, val) => {
    const next = form.inclusions.map((row, i) => (i === idx ? { ...row, [key]: val } : row));
    setForm({ ...form, inclusions: next });
  };
  const addInc = () => setForm({ ...form, inclusions: [...form.inclusions, blankInclusion()] });
  const rmInc = (idx) => setForm({ ...form, inclusions: form.inclusions.filter((_, i) => i !== idx) });

  const toggleTableType = (tt) => {
    const has = form.allowed_table_types.includes(tt);
    setForm({
      ...form,
      allowed_table_types: has
        ? form.allowed_table_types.filter((x) => x !== tt)
        : [...form.allowed_table_types, tt],
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const inclusions = form.inclusions
        .filter((i) => i.product_id)  // drop legacy/blank rows
        .map((i) => ({
          product_id: i.product_id,
          quantity: parseInt(i.quantity) || 1,
          notes: i.notes || "",
        }));
      const payload = {
        code: form.code.trim().toUpperCase(),
        name: form.name.trim(),
        description: form.description,
        minimum_spend: parseFloat(form.minimum_spend) || 0,
        required_deposit: parseFloat(form.required_deposit) || 0,
        guest_limit: parseInt(form.guest_limit) || 0,
        vip_only: !!form.vip_only,
        active: !!form.active,
        allowed_table_types: form.allowed_table_types,
        event_id: form.event_id || null,
        valid_from: form.valid_from || null,
        valid_to: form.valid_to || null,
        inclusions,
      };
      if (editing) {
        await api.patch(`/vip-packages/${editing.id}`, payload);
        toast.success("Package updated");
      } else {
        await api.post("/vip-packages", { ...payload, branch_id: form.branch_id });
        toast.success("Package created");
      }
      setCreating(false); setEditing(null); setForm(buildBlank(branchId)); load();
    } catch (e2) { toast.error(formatApiError(e2.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const deactivate = async (p) => {
    if (!window.confirm(`Deactivate package '${p.name}'? Existing reservations keep their snapshot.`)) return;
    try {
      await api.delete(`/vip-packages/${p.id}`);
      toast.success("Package deactivated");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const branchesById = useMemo(() => Object.fromEntries(branches.map((b) => [b.id, b])), [branches]);
  const productsById = useMemo(() => Object.fromEntries(products.map((p) => [p.id, p])), [products]);
  const active = packages.filter((p) => p.active);
  const inactive = packages.filter((p) => !p.active);

  return (
    <div className="p-8 lg:p-14 min-h-full" data-testid="vip-packages-page">
      <div className="flex flex-wrap items-start justify-between gap-8">
        <div>
          <div className="section-eyebrow ink-subtle">Reservations · VIP</div>
          <h1 className="mt-4 display-heading text-4xl md:text-5xl ink">VIP Packages</h1>
          <p className="mt-3 ink-muted text-sm max-w-md leading-relaxed">
            Branch-scoped VIP bundles with product-backed inclusions, optional event
            binding, validity window and allowed table types. Snapshot is frozen onto
            each reservation the moment you attach a package.
          </p>
        </div>
        {canManage && (
          <button onClick={openCreate} data-testid="vip-pkg-new-btn"
                   className="btn-pill btn-pill-solid h-11">
            <Plus size={14}/> New package
          </button>
        )}
      </div>

      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {active.map((p) => (
          <PackageCard key={p.id} pkg={p} canManage={canManage}
                        branchesById={branchesById} productsById={productsById}
                        onEdit={() => openEdit(p)}
                        onDeactivate={() => deactivate(p)}/>
        ))}
        {active.length === 0 && (
          <div className="col-span-full py-16 text-center border hairline-soft rounded-md ink-subtle text-sm"
                data-testid="vip-pkg-empty">
            No active VIP packages yet. {canManage && "Click 'New package' to create one."}
          </div>
        )}
      </div>

      {inactive.length > 0 && (
        <details className="mt-10">
          <summary className="tiny-label cursor-pointer ink-subtle hover:ink">
            Show {inactive.length} inactive
          </summary>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 opacity-60">
            {inactive.map((p) => (
              <PackageCard key={p.id} pkg={p} canManage={canManage}
                            branchesById={branchesById} productsById={productsById}
                            onEdit={() => openEdit(p)} onDeactivate={null}/>
            ))}
          </div>
        </details>
      )}

      {creating && (
        <PackageDrawer form={form} setForm={setForm} editing={editing}
                        branches={branches} products={products} events={events}
                        setIncField={setIncField} addInc={addInc} rmInc={rmInc}
                        toggleTableType={toggleTableType}
                        onCancel={() => { setCreating(false); setEditing(null); setForm(buildBlank(branchId)); }}
                        onSubmit={submit} busy={busy}/>
      )}
    </div>
  );
}


function PackageCard({ pkg, canManage, branchesById, productsById, onEdit, onDeactivate }) {
  const branch = branchesById?.[pkg.branch_id];
  return (
    <div className="p-5 border hairline surface-card rounded-lg" data-testid={`vip-pkg-card-${pkg.code}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="tiny-label mono">{pkg.code}</span>
            {branch && (
              <span className="text-[9px] uppercase tracking-[0.22em] ink-subtle">{branch.code}</span>
            )}
            {pkg.vip_only && (
              <span className="text-[9px] uppercase tracking-[0.22em] font-bold px-2 py-0.5 rounded-full border text-amber-500 border-amber-500/40 bg-amber-500/5">
                VIP only
              </span>
            )}
          </div>
          <div className="mt-1 display-heading text-xl ink truncate">{pkg.name}</div>
          {pkg.description && (
            <p className="mt-2 text-xs ink-subtle leading-relaxed line-clamp-2">{pkg.description}</p>
          )}
        </div>
        <Star size={16} className="text-amber-500 shrink-0"/>
      </div>
      <div className="mt-4 grid grid-cols-2 gap-3 text-xs">
        <F icon={<Wallet size={12}/>} k="Min spend" v={peso(pkg.minimum_spend)}/>
        <F icon={<Wallet size={12}/>} k="Deposit" v={peso(pkg.required_deposit)}/>
        <F icon={<Users size={12}/>} k="Guest limit" v={pkg.guest_limit > 0 ? `${pkg.guest_limit} pax` : "—"}/>
        <F icon={<Sparkles size={12}/>} k="Inclusions" v={pkg.inclusions?.length ? `${pkg.inclusions.length}` : "—"}/>
      </div>
      {(pkg.allowed_table_types?.length > 0 || pkg.event_id || pkg.valid_from || pkg.valid_to) && (
        <div className="mt-3 text-xs ink-subtle space-y-1 border-t hairline-soft pt-3">
          {pkg.allowed_table_types?.length > 0 && (
            <div className="flex items-center gap-1">
              <Tag size={12}/> Tables: {pkg.allowed_table_types.join(", ")}
            </div>
          )}
          {(pkg.valid_from || pkg.valid_to) && (
            <div className="flex items-center gap-1">
              <Calendar size={12}/> Valid {pkg.valid_from ? new Date(pkg.valid_from).toLocaleDateString() : "—"} → {pkg.valid_to ? new Date(pkg.valid_to).toLocaleDateString() : "—"}
            </div>
          )}
          {pkg.event_id && <div className="mono truncate">Event: {pkg.event_id.slice(-8)}</div>}
        </div>
      )}
      {pkg.inclusions?.length > 0 && (
        <ul className="mt-3 space-y-1 text-xs ink-subtle border-t hairline-soft pt-3">
          {pkg.inclusions.slice(0, 3).map((i, idx) => {
            const p = i.product_id ? productsById?.[i.product_id] : null;
            const label = i.product_name || p?.name || i.name || "Legacy inclusion";
            return (
              <li key={idx} className="truncate" data-testid={`vip-pkg-card-inc-${idx}`}>
                · {i.quantity} × {label}
                {!i.product_id && (
                  <span className="ml-1 text-[9px] uppercase tracking-widest ink-subtle">legacy</span>
                )}
              </li>
            );
          })}
          {pkg.inclusions.length > 3 && (
            <li className="ink-subtle">+ {pkg.inclusions.length - 3} more</li>
          )}
        </ul>
      )}
      {canManage && (
        <div className="mt-4 flex gap-2">
          <button onClick={onEdit} data-testid={`vip-pkg-edit-${pkg.code}`}
                   className="btn-pill btn-pill-outline h-9 text-xs flex-1">
            <Pencil size={12}/> Edit
          </button>
          {onDeactivate && (
            <button onClick={onDeactivate} data-testid={`vip-pkg-deactivate-${pkg.code}`}
                     className="btn-pill btn-pill-outline h-9 text-xs text-red-500 border-red-500/40 hover:bg-red-500/5">
              <Trash2 size={12}/> Deactivate
            </button>
          )}
        </div>
      )}
    </div>
  );
}


function PackageDrawer({ form, setForm, editing, branches, products, events,
                          setIncField, addInc, rmInc, toggleTableType,
                          onCancel, onSubmit, busy }) {
  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="vip-pkg-drawer">
      <div className="absolute inset-0 bg-black/60" onClick={onCancel}/>
      <form onSubmit={onSubmit} className="relative w-full max-w-lg surface-card border-l hairline h-full p-6 overflow-y-auto flex flex-col">
        <div className="flex items-center justify-between">
          <div>
            <div className="section-eyebrow">{editing ? "Edit" : "New"} package</div>
            <h3 className="mt-2 display-heading text-2xl ink">{editing ? editing.name : "VIP package"}</h3>
          </div>
          <button type="button" onClick={onCancel} className="ink-subtle hover:ink"><X size={18}/></button>
        </div>

        <div className="mt-6 grid grid-cols-2 gap-3">
          <label className="col-span-2">
            <div className="tiny-label">Branch</div>
            <select required disabled={!!editing} value={form.branch_id}
                     onChange={(e) => setForm({ ...form, branch_id: e.target.value })}
                     data-testid="vip-pkg-branch"
                     className="mt-1 h-11 w-full rounded-md border hairline surface-2 px-3 ink">
              <option value="">Select branch…</option>
              {branches.map((b) => (
                <option key={b.id} value={b.id}>{b.code} · {b.name}</option>
              ))}
            </select>
          </label>
          <label className="col-span-1">
            <div className="tiny-label">Code</div>
            <input required disabled={!!editing} value={form.code}
                    onChange={(e) => setForm({ ...form, code: e.target.value })}
                    data-testid="vip-pkg-code"
                    className="mt-1 h-11 w-full rounded-md border hairline surface-2 px-3 mono ink uppercase"/>
          </label>
          <label className="col-span-1">
            <div className="tiny-label">Name</div>
            <input required value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    data-testid="vip-pkg-name"
                    className="mt-1 h-11 w-full rounded-md border hairline surface-2 px-3 ink"/>
          </label>
          <label className="col-span-2">
            <div className="tiny-label">Description</div>
            <textarea value={form.description} rows={2}
                       onChange={(e) => setForm({ ...form, description: e.target.value })}
                       data-testid="vip-pkg-desc"
                       className="mt-1 w-full rounded-md border hairline surface-2 px-3 py-2 ink text-sm"/>
          </label>
          <label>
            <div className="tiny-label">Minimum spend (₱)</div>
            <input required type="number" min={0} step={100} value={form.minimum_spend}
                    onChange={(e) => setForm({ ...form, minimum_spend: e.target.value })}
                    data-testid="vip-pkg-min-spend"
                    className="mt-1 h-11 w-full rounded-md border hairline surface-2 px-3 mono ink"/>
          </label>
          <label>
            <div className="tiny-label">Required deposit (₱)</div>
            <input required type="number" min={0} step={100} value={form.required_deposit}
                    onChange={(e) => setForm({ ...form, required_deposit: e.target.value })}
                    data-testid="vip-pkg-deposit"
                    className="mt-1 h-11 w-full rounded-md border hairline surface-2 px-3 mono ink"/>
          </label>
          <label>
            <div className="tiny-label">Guest limit (0 = unlimited)</div>
            <input required type="number" min={0} value={form.guest_limit}
                    onChange={(e) => setForm({ ...form, guest_limit: e.target.value })}
                    data-testid="vip-pkg-guest-limit"
                    className="mt-1 h-11 w-full rounded-md border hairline surface-2 px-3 mono ink"/>
          </label>
          <label className="flex items-end gap-2">
            <input type="checkbox" checked={form.vip_only}
                    onChange={(e) => setForm({ ...form, vip_only: e.target.checked })}
                    data-testid="vip-pkg-vip-only"
                    className="mb-3 h-4 w-4"/>
            <span className="tiny-label mb-3">VIP tables only</span>
          </label>

          <div className="col-span-2">
            <div className="tiny-label">Allowed table types</div>
            <div className="mt-2 flex flex-wrap gap-2">
              {ALLOWED_TABLE_TYPES.map((tt) => {
                const on = form.allowed_table_types.includes(tt);
                return (
                  <button type="button" key={tt} onClick={() => toggleTableType(tt)}
                           data-testid={`vip-pkg-ttype-${tt}`}
                           className={`text-xs px-3 h-8 rounded-full border transition-colors ${on ? "bg-foreground text-background border-foreground" : "hairline hover:bg-foreground/5"}`}>
                    {tt}
                  </button>
                );
              })}
            </div>
            <div className="tiny-label mt-1 ink-subtle">Empty = allow any table (subject to VIP-only flag).</div>
          </div>

          <label>
            <div className="tiny-label">Valid from</div>
            <input type="datetime-local" value={form.valid_from ? form.valid_from.slice(0, 16) : ""}
                    onChange={(e) => setForm({ ...form, valid_from: e.target.value ? new Date(e.target.value).toISOString() : "" })}
                    data-testid="vip-pkg-valid-from"
                    className="mt-1 h-11 w-full rounded-md border hairline surface-2 px-3 ink text-sm"/>
          </label>
          <label>
            <div className="tiny-label">Valid to</div>
            <input type="datetime-local" value={form.valid_to ? form.valid_to.slice(0, 16) : ""}
                    onChange={(e) => setForm({ ...form, valid_to: e.target.value ? new Date(e.target.value).toISOString() : "" })}
                    data-testid="vip-pkg-valid-to"
                    className="mt-1 h-11 w-full rounded-md border hairline surface-2 px-3 ink text-sm"/>
          </label>
          <label className="col-span-2">
            <div className="tiny-label">Event (optional — locks package to a single event)</div>
            <select value={form.event_id}
                     onChange={(e) => setForm({ ...form, event_id: e.target.value })}
                     data-testid="vip-pkg-event"
                     className="mt-1 h-11 w-full rounded-md border hairline surface-2 px-3 ink">
              <option value="">— none —</option>
              {events.filter((ev) => !form.branch_id || ev.branch_id === form.branch_id).map((ev) => (
                <option key={ev.id} value={ev.id}>{ev.name}</option>
              ))}
            </select>
          </label>
        </div>

        <div className="mt-6">
          <div className="flex items-center justify-between">
            <div className="section-eyebrow">Inclusions (product-backed)</div>
            <button type="button" onClick={addInc} data-testid="vip-pkg-add-inclusion"
                     className="btn-pill btn-pill-outline h-8 text-xs"><Plus size={12}/> Add</button>
          </div>
          <div className="mt-3 space-y-2">
            {form.inclusions.map((inc, idx) => {
              const isLegacy = !!inc._legacy_name;
              return (
                <div key={idx} className="flex gap-2 items-start" data-testid={`vip-pkg-inclusion-${idx}`}>
                  <input type="number" min={1} value={inc.quantity}
                          onChange={(e) => setIncField(idx, "quantity", e.target.value)}
                          placeholder="Qty"
                          className="h-10 w-16 rounded-md border hairline surface-2 px-2 mono ink text-center"/>
                  {isLegacy ? (
                    <div className="flex-1 h-10 flex items-center px-3 rounded-md border hairline surface-2 text-sm ink-subtle italic">
                      {inc._legacy_name} <span className="ml-2 text-[9px] uppercase tracking-widest">legacy</span>
                    </div>
                  ) : (
                    <select value={inc.product_id}
                             onChange={(e) => setIncField(idx, "product_id", e.target.value)}
                             data-testid={`vip-pkg-inclusion-product-${idx}`}
                             className="h-10 flex-1 rounded-md border hairline surface-2 px-2 ink text-sm">
                      <option value="">Choose product…</option>
                      {products.map((p) => (
                        <option key={p.id} value={p.id}>{p.name} · {peso(p.price)}</option>
                      ))}
                    </select>
                  )}
                  <input value={inc.notes}
                          onChange={(e) => setIncField(idx, "notes", e.target.value)}
                          placeholder="notes"
                          className="h-10 w-28 rounded-md border hairline surface-2 px-2 ink text-xs"/>
                  <button type="button" onClick={() => rmInc(idx)}
                           className="h-10 px-2 ink-subtle hover:text-red-500"><X size={14}/></button>
                </div>
              );
            })}
          </div>
        </div>

        <div className="mt-auto pt-6 flex justify-end gap-2">
          <button type="button" onClick={onCancel} className="btn-pill btn-pill-outline h-11">Cancel</button>
          <button type="submit" disabled={busy || !form.branch_id}
                   data-testid="vip-pkg-save-btn"
                   className="btn-pill btn-pill-solid h-11 disabled:opacity-40">
            {busy ? "Saving…" : (editing ? "Save changes" : "Create package")}
          </button>
        </div>
      </form>
    </div>
  );
}


function F({ icon, k, v }) {
  return (
    <div>
      <div className="tiny-label flex items-center gap-1">{icon}{k}</div>
      <div className="mono text-sm ink font-semibold mt-1">{v}</div>
    </div>
  );
}
